import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      // Preserve selling_plan parameter if it exists
      const currentSellingPlan = url.searchParams.get('selling_plan');
      if (!currentSellingPlan) {
        // Try to get from form if not in URL
        const productForm = this.closest('form[data-type="add-to-cart-form"]');
        if (productForm) {
          const hiddenSellingPlan = productForm.querySelector('input[name="selling_plan"]');
          const sellingPlanId = hiddenSellingPlan?.value;
          if (sellingPlanId) {
            url.searchParams.set('selling_plan', sellingPlanId);
          }
        }
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // Include selling_plan parameter if present in URL or form
    // This is critical for server-side badge rendering to use correct subscription prices
    let sellingPlanId = null;

    // First, try to get from current URL
    const currentUrl = new URL(window.location.href);
    sellingPlanId = currentUrl.searchParams.get('selling_plan');

    // If not in URL, try to get from product form
    if (!sellingPlanId) {
      const productForm = this.closest('form[data-type="add-to-cart-form"]');
      if (productForm) {
        // Try multiple selectors to handle different Recharge/Shopify subscription implementations
        const sellingPlanInput = productForm.querySelector(
          'input[name="selling_plan"]:checked, input[type="radio"][name*="selling_plan"]:checked, select[name="selling_plan"], input[data-selling-plan]:checked, [data-selling-plan-id]:checked, input[name="selling_plan"][value]:not([value=""]), input[type="hidden"][name="selling_plan"]'
        );
        if (sellingPlanInput) {
          sellingPlanId = sellingPlanInput.value || sellingPlanInput.dataset?.sellingPlanId || sellingPlanInput.dataset?.sellingPlan;
        }

        // Also check for Recharge-specific inputs
        if (!sellingPlanId) {
          const rechargeInput = productForm.querySelector('[name*="properties[subscription_id]"], [name*="properties[subscription_plan_id]"]');
          if (rechargeInput && rechargeInput.value) {
            sellingPlanId = rechargeInput.value;
          }
        }
      }
    }

    // Fallback: try localStorage (set by ProductPrice component)
    if (!sellingPlanId) {
      try {
        const stored = localStorage.getItem('productPurchaseOption');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.purchaseOption === 'subscription' && parsed.sellingPlanId) {
            sellingPlanId = parsed.sellingPlanId;
          }
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }

    // If we found a selling plan ID, include it in the request
    if (sellingPlanId) {
      params.push(`selling_plan=${sellingPlanId}`);
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          // Update saving badges in the parsed HTML BEFORE morph renders it
          // This prevents the flash of one-time prices when subscription is selected
          await this.updateSavingBadgesInHtml(html);

          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        // Silently handle errors
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Updates saving badges in the parsed HTML document BEFORE morph renders it.
   * This ensures badges show the correct savings based on the current purchase option
   * (subscription vs one-time), preventing any flash of incorrect values.
   * @param {Document} htmlDoc - The parsed HTML document to update
   * @returns {Promise<void>} Resolves when all badge updates are complete
   */
  async updateSavingBadgesInHtml(htmlDoc) {
    // Get purchase option state from DOM or localStorage
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!productForm) return;

    const subscriptionRadio = productForm.querySelector('input[name="purchaseOption"][value="subscription"]');
    const oneTimeRadio = productForm.querySelector('input[name="purchaseOption"][value="onetime"]');

    // Determine if subscription is selected
    let isSubscriptionSelected = false;

    // Check radio button states
    if (subscriptionRadio?.checked === true) {
      isSubscriptionSelected = true;
    } else if (oneTimeRadio?.checked === true) {
      isSubscriptionSelected = false;
    } else {
      // Check URL parameter as fallback
      const urlParams = new URLSearchParams(window.location.search);
      const urlSellingPlan = urlParams.get('selling_plan');
      if (urlSellingPlan) {
        isSubscriptionSelected = true;
      } else {
        // Check localStorage as last resort
        try {
          const productId = this.dataset.productId;
          if (productId) {
            const stored = localStorage.getItem(`purchaseOption_${productId}`);
            if (stored) {
              const parsed = JSON.parse(stored);
              isSubscriptionSelected = parsed.purchaseOption === 'subscription';
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    }

    // Find the variant picker in the parsed HTML document
    const newVariantPicker = htmlDoc.querySelector(this.tagName.toLowerCase());
    if (!newVariantPicker) return;

    // Get all saving badges - server renders subscription savings by default
    const savingBadges = newVariantPicker.querySelectorAll('.variant-option__saving-badge');
    if (savingBadges.length === 0) return;

    // Process each badge - update based on current purchase option
    const badgeElements = /** @type {HTMLElement[]} */ (Array.from(savingBadges));
    const badgeUpdatePromises = badgeElements.map(async (badge) => {
      const variantId = badge.getAttribute('data-variant-id');
      if (!variantId) return;

      // Fetch variant data to get prices and allocations
      const sectionId = this.dataset.sectionId;
      const productUrl = this.dataset.productUrl || window.location.pathname;

      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        if (sectionId) {
          url.searchParams.set('section_id', sectionId);
        }

        const response = await fetch(url.toString());
        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const variantScript = doc.querySelector('variant-picker script[type="application/json"]');

        if (!variantScript) return;

        const variantData = JSON.parse(variantScript.textContent);
        const regularPrice = variantData.price;
        const compareAtPrice = variantData.compare_at_price || 0;

        let savings = 0;

        if (isSubscriptionSelected) {
          // Subscription selected - show subscription savings
          if (variantData.selling_plan_allocations && variantData.selling_plan_allocations.length > 0) {
            const allocation = variantData.selling_plan_allocations[0];
            const subscriptionPrice = allocation.price;

            // For subscriptions: first try compare_at_price (MSRP savings)
            // Fall back to regular price if compare_at doesn't provide savings
            if (compareAtPrice > subscriptionPrice) {
              savings = compareAtPrice - subscriptionPrice;
            } else if (regularPrice > subscriptionPrice) {
              // Fallback: use regular variant price if compare_at doesn't provide savings
              savings = regularPrice - subscriptionPrice;
            }
          }
        } else {
          // One-time purchase selected - show one-time savings (compare_at vs regular price)
          if (compareAtPrice > regularPrice) {
            savings = compareAtPrice - regularPrice;
          }
        }

        if (savings > 0) {
          // Format price (simple format, cents to dollars)
          const formattedSavings = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(savings / 100);

          badge.textContent = `Save ${formattedSavings}`;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      } catch (error) {
        // Silently fail - badge will keep server-rendered value
      }
    });

    // Wait for all badge updates to complete before returning
    await Promise.all(badgeUpdatePromises);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
